import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { today, type CalendarDate } from '@internationalized/date';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import {
  PaginationDirection,
  TransactionAnomalyPriority as TransactionAnomalyPriorityEnum,
  type TransactionFilters,
} from '../../__generated__/globalTypes';
import Header from '../../components/Header';

import AnomalyScoreRanges from './AnomalyScoreRanges';
import GlobalStatistics from './GlobalStatistics';
import PrioritySection, { CollapsibleSection } from './PrioritySection';
import {
  PRIORITY_ORDER,
  PRIORITY_TONE,
  resolvePriority,
  type IPriorityBuckets,
  type MerchantStatsRow,
  type TransactionNode,
} from './transactionAnomalyFeedbackTypes';
import TransactionPairs from './TransactionPairs';

import type {
  AdminCreateTransactionAnomalyFeedbackMutation,
  AdminCreateTransactionAnomalyFeedbackMutationVariables,
  AdminDeleteTransactionAnomalyFeedbackMutation,
  AdminDeleteTransactionAnomalyFeedbackMutationVariables,
  AdminGetTransactionsForReviewQuery,
  AdminGetTransactionsForReviewQueryVariables,
  AdminGetUserQuery,
  AdminGetUserQueryVariables,
} from './__generated__/TransactionAnomalyFeedbackScene.gql';

const TRANSACTIONS_PER_PAGE = 500;

const TRANSACTION_FRAGMENT = gql`
  fragment TransactionFragment on Transaction {
    id
    amount
    date
    description
    rawJson
    merchant {
      id
      name
    }
    category {
      id
      type
      detailedType
    }
    financialAccount {
      id
      name
    }
    anomalyDetection {
      id
      anomalyScore
      anomalyReasons
      scoreParts {
        baseScore
        merchantAdjustment
        timingAdjustment
        financialAccountAdjustment
        finalScore
      }
      featuresJson
    }
    anomalyFeedback {
      id
      priority
      detailedReason
    }
    pairedTransaction {
      id
      amount
      date
      description
      merchant {
        id
        name
      }
      financialAccount {
        id
        name
      }
    }
  }
`;

const ADMIN_GET_TRANSACTIONS_FOR_REVIEW = gql`
  ${TRANSACTION_FRAGMENT}
  query AdminGetTransactionsForReview($userId: String!, $pagination: PaginationInput!, $filters: TransactionFilters) {
    adminGetTransactionsForUser(userId: $userId, pagination: $pagination, filters: $filters) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...TransactionFragment
        }
      }
    }
    adminGetLatestTransactionMerchantStatistics(userId: $userId) {
      id
      merchant {
        id
        name
      }
      startDate
      endDate
      statsJson
    }
  }
`;

const CREATE_TRANSACTION_ANOMALY_FEEDBACK = gql`
  ${TRANSACTION_FRAGMENT}
  mutation AdminCreateTransactionAnomalyFeedback($input: AdminCreateTransactionAnomalyFeedbackInput!) {
    adminCreateTransactionAnomalyFeedback(input: $input) {
      ...TransactionFragment
    }
  }
`;

const DELETE_TRANSACTION_ANOMALY_FEEDBACK = gql`
  ${TRANSACTION_FRAGMENT}
  mutation AdminDeleteTransactionAnomalyFeedback($transactionId: ID!, $userId: ID!) {
    adminDeleteTransactionAnomalyFeedback(transactionId: $transactionId, userId: $userId) {
      ...TransactionFragment
    }
  }
`;

const ADMIN_GET_USER = gql`
  query AdminGetUser($id: ID!) {
    adminGetUser(id: $id) {
      id
      name
      email
    }
  }
`;

function isInDateRange(transaction: TransactionNode, startDate: CalendarDate, endDate: CalendarDate): boolean {
  const date = transaction.date;
  return date.compare(startDate) >= 0 && date.compare(endDate) <= 0;
}

/**
 * Per-user transaction anomaly review scene. Loads up to a month of
 * transactions in `TRANSACTIONS_PER_PAGE` chunks, groups them by priority,
 * and lets the operator mark each row reviewed (with optional detailed
 * reason) or bulk-mark all rows in a priority. Mutations create/delete
 * callouts on the consumer product based on the chosen priority.
 */
const TransactionAnomalyFeedbackScene = () => {
  const { userId = '' } = useParams<{ userId: string }>();

  const { data: userData } = useQuery<AdminGetUserQuery, AdminGetUserQueryVariables>(ADMIN_GET_USER, {
    variables: { id: userId },
    skip: !userId,
  });

  const [fetchPage, { loading: pageLoading, error: pageError }] = useLazyQuery<
    AdminGetTransactionsForReviewQuery,
    AdminGetTransactionsForReviewQueryVariables
  >(ADMIN_GET_TRANSACTIONS_FOR_REVIEW);

  const [allTransactions, setAllTransactions] = React.useState<TransactionNode[]>([]);
  const [merchantStats, setMerchantStats] = React.useState<MerchantStatsRow[]>([]);
  const [isFetchingAllPages, setIsFetchingAllPages] = React.useState(false);

  // Anchor "today" once for the lifetime of the scene so that a midnight UTC
  // rollover between the fetch effect and the bucketing memo cannot shift
  // the date range and silently drop a row.
  const todayDate = React.useMemo(() => today('UTC'), []);
  const oneMonthAgo = React.useMemo(() => todayDate.subtract({ months: 1 }), [todayDate]);

  React.useEffect(() => {
    if (!userId) {
      return;
    }
    let cancelled = false;
    const run = async () => {
      setIsFetchingAllPages(true);
      const collected: TransactionNode[] = [];
      let cursor: string | null = null;
      let hasNextPage = true;
      let stats: MerchantStatsRow[] = [];

      const filters: TransactionFilters = {
        startDate: oneMonthAgo.toString() as unknown as CalendarDate,
        endDate: todayDate.toString() as unknown as CalendarDate,
      };

      while (hasNextPage && !cancelled) {
        const result = await fetchPage({
          variables: {
            userId,
            pagination: { cursor, limit: TRANSACTIONS_PER_PAGE, direction: PaginationDirection.Forward },
            filters,
          },
        });
        if (result.error) {
          break;
        }
        const data = result.data;
        if (!data) {
          break;
        }
        collected.push(...data.adminGetTransactionsForUser.edges.map((edge) => edge.node));
        if (stats.length === 0) {
          stats = data.adminGetLatestTransactionMerchantStatistics ?? [];
        }
        hasNextPage = data.adminGetTransactionsForUser.pageInfo.hasNextPage;
        cursor = data.adminGetTransactionsForUser.pageInfo.endCursor ?? null;
      }
      if (!cancelled) {
        setAllTransactions(collected);
        setMerchantStats(stats);
        setIsFetchingAllPages(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [userId, fetchPage, todayDate, oneMonthAgo]);

  const merchantStatsById = React.useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    for (const stat of merchantStats) {
      if (stat.merchant?.id) {
        map.set(stat.merchant.id, stat.statsJson as Record<string, unknown>);
      }
    }
    return map;
  }, [merchantStats]);

  const [priorityVisibility, setPriorityVisibility] = React.useState<Record<TransactionAnomalyPriorityEnum, boolean>>({
    [TransactionAnomalyPriorityEnum.P0]: true,
    [TransactionAnomalyPriorityEnum.P1]: false,
    [TransactionAnomalyPriorityEnum.P2]: false,
    [TransactionAnomalyPriorityEnum.P3]: false,
    [TransactionAnomalyPriorityEnum.P4]: false,
  });
  const [pairsVisible, setPairsVisible] = React.useState(false);
  const [detailedReasonMap, setDetailedReasonMap] = React.useState<Map<string, string>>(new Map());
  const [mutationError, setMutationError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDetailedReasonMap((prev) => {
      const next = new Map(prev);
      for (const t of allTransactions) {
        if (t.anomalyFeedback?.detailedReason && !prev.has(t.id)) {
          next.set(t.id, t.anomalyFeedback.detailedReason);
        }
      }
      return next;
    });
  }, [allTransactions]);

  const handleDetailedReasonChange = React.useCallback((transactionId: string, value: string) => {
    setDetailedReasonMap((prev) => {
      const next = new Map(prev);
      next.set(transactionId, value);
      return next;
    });
  }, []);

  // The `update` callbacks below ignore the Apollo cache (the scene drives its
  // display off `allTransactions` local state, since it accumulates pages
  // outside the normalized cache) and instead patch local state with the
  // mutation result. Naming `_cache` reflects this — we are deliberately
  // skipping the cache write in favor of the local accumulator.
  const [createFeedback] = useMutation<
    AdminCreateTransactionAnomalyFeedbackMutation,
    AdminCreateTransactionAnomalyFeedbackMutationVariables
  >(CREATE_TRANSACTION_ANOMALY_FEEDBACK, {
    update: (_cache, { data }) => {
      if (!data?.adminCreateTransactionAnomalyFeedback) {
        return;
      }
      setAllTransactions((prev) => {
        const map = new Map(prev.map((t) => [t.id, t]));
        for (const updated of data.adminCreateTransactionAnomalyFeedback) {
          map.set(updated.id, updated);
        }
        return Array.from(map.values());
      });
    },
  });

  const [deleteFeedback] = useMutation<
    AdminDeleteTransactionAnomalyFeedbackMutation,
    AdminDeleteTransactionAnomalyFeedbackMutationVariables
  >(DELETE_TRANSACTION_ANOMALY_FEEDBACK, {
    update: (_cache, { data }) => {
      if (!data?.adminDeleteTransactionAnomalyFeedback) {
        return;
      }
      setAllTransactions((prev) => {
        const map = new Map(prev.map((t) => [t.id, t]));
        map.set(data.adminDeleteTransactionAnomalyFeedback.id, data.adminDeleteTransactionAnomalyFeedback);
        return Array.from(map.values());
      });
    },
  });

  const grouped = React.useMemo<Record<TransactionAnomalyPriorityEnum, IPriorityBuckets>>(() => {
    const empty = (): IPriorityBuckets => ({ unreviewed: [], reviewed: [] });
    const buckets: Record<TransactionAnomalyPriorityEnum, IPriorityBuckets> = {
      [TransactionAnomalyPriorityEnum.P0]: empty(),
      [TransactionAnomalyPriorityEnum.P1]: empty(),
      [TransactionAnomalyPriorityEnum.P2]: empty(),
      [TransactionAnomalyPriorityEnum.P3]: empty(),
      [TransactionAnomalyPriorityEnum.P4]: empty(),
    };
    if (allTransactions.length === 0) {
      return buckets;
    }
    for (const transaction of allTransactions) {
      if (!isInDateRange(transaction, oneMonthAgo, todayDate)) {
        continue;
      }
      if (!transaction.anomalyDetection) {
        continue;
      }
      const priority = resolvePriority(transaction);
      const bucket = transaction.anomalyFeedback ? buckets[priority].reviewed : buckets[priority].unreviewed;
      bucket.push(transaction);
    }
    return buckets;
  }, [allTransactions, oneMonthAgo, todayDate]);

  const handleToggleReviewed = React.useCallback(
    async (transaction: TransactionNode, checked: boolean) => {
      if (!userId) {
        return;
      }
      try {
        if (checked) {
          if (transaction.anomalyFeedback) {
            return;
          }
          const priority = resolvePriority(transaction);
          const detailedReason = detailedReasonMap.get(transaction.id) || null;
          await createFeedback({
            variables: { input: { userId, transactionIds: [transaction.id], priority, detailedReason } },
          });
        } else {
          await deleteFeedback({ variables: { transactionId: transaction.id, userId } });
        }
        setMutationError(null);
      } catch (e) {
        setMutationError(e instanceof Error ? e.message : 'Failed to update feedback');
      }
    },
    [userId, createFeedback, deleteFeedback, detailedReasonMap]
  );

  const handleSavePriority = React.useCallback(
    async (transactionId: string, priority: TransactionAnomalyPriorityEnum, detailedReason: string | null) => {
      if (!userId) {
        return;
      }
      try {
        await createFeedback({
          variables: { input: { userId, transactionIds: [transactionId], priority, detailedReason } },
        });
        setMutationError(null);
      } catch (e) {
        setMutationError(e instanceof Error ? e.message : 'Failed to save priority');
      }
    },
    [userId, createFeedback]
  );

  const handleMarkAll = React.useCallback(
    async (transactionIds: string[], priority: TransactionAnomalyPriorityEnum) => {
      if (!userId || transactionIds.length === 0) {
        return;
      }
      try {
        await createFeedback({
          variables: { input: { userId, transactionIds, priority, detailedReason: null } },
        });
        setMutationError(null);
      } catch (e) {
        setMutationError(e instanceof Error ? e.message : 'Failed to mark transactions');
      }
    },
    [userId, createFeedback]
  );

  const isLoading = pageLoading || isFetchingAllPages;

  if (!userId) {
    return (
      <div className="bg-bg-primary text-fg-error-primary flex min-h-screen items-center justify-center">
        <span>Error: User ID is required.</span>
      </div>
    );
  }

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Transaction anomaly feedback</h1>
          {userData?.adminGetUser ? (
            <div className="text-fg-secondary text-xs">
              Reviewing <span className="text-fg-primary font-medium">{userData.adminGetUser.name}</span>{' '}
              <span className="text-fg-tertiary font-mono">({userData.adminGetUser.email})</span>
            </div>
          ) : null}
          <p className="text-fg-tertiary text-xs">Last month of transactions, grouped by detected priority.</p>
        </div>

        <AnomalyScoreRanges />
        <GlobalStatistics userId={userId} />

        {pageError ? (
          <span className="text-fg-error-primary text-xs">Error loading transactions: {pageError.message}</span>
        ) : null}
        {mutationError ? (
          <span className="text-fg-error-primary text-xs">Error saving feedback: {mutationError}</span>
        ) : null}
        {isLoading ? <span className="text-fg-secondary text-xs">Loading transactions…</span> : null}

        {!isLoading &&
          PRIORITY_ORDER.map((priority) => {
            const { unreviewed, reviewed } = grouped[priority];
            if (unreviewed.length === 0 && reviewed.length === 0) {
              return null;
            }
            return (
              <PrioritySection
                key={priority}
                priority={priority}
                unreviewed={unreviewed}
                reviewed={reviewed}
                isOpen={priorityVisibility[priority]}
                onToggle={() =>
                  setPriorityVisibility((prev) => ({
                    ...prev,
                    [priority]: !prev[priority],
                  }))
                }
                merchantStatsById={merchantStatsById}
                detailedReasonMap={detailedReasonMap}
                onDetailedReasonChange={handleDetailedReasonChange}
                onToggleReviewed={handleToggleReviewed}
                onSavePriority={handleSavePriority}
                onMarkAll={handleMarkAll}
              />
            );
          })}

        <CollapsibleSection
          title="Transaction pairs"
          isOpen={pairsVisible}
          onToggle={() => setPairsVisible((prev) => !prev)}
          tone={PRIORITY_TONE[TransactionAnomalyPriorityEnum.P4]}
        >
          <TransactionPairs userId={userId} />
        </CollapsibleSection>
      </main>
    </div>
  );
};

export default TransactionAnomalyFeedbackScene;
